import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as R from 'ramda';
import { Avatar, Box, Chip, Collapse, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  KeyboardArrowDown,
  KeyboardArrowRight,
} from '@material-ui/icons';
import { green, grey, orange, red } from '@material-ui/core/colors';

import {
  formatQuestionType,
  getOptionLabel,
  getQuestionAverages,
  getQuestionCount,
  getQuestionOptions,
  getQuizQuestion,
  getStudentChoices,
  getSubmissionQuestion,
  isMultipleChoiceQuestion,
  isOptionCorrectAnswer,
} from 'resources/common/quizProgress/questionData';

const useStyles = makeStyles({
  root: {
    padding: '16px 24px 24px',
    backgroundColor: '#fafafa',
  },
  questionCard: {
    border: `1px solid ${grey[300]}`,
    borderRadius: 8,
    padding: '16px 20px',
    marginBottom: 12,
    backgroundColor: '#fff',
  },
  questionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    cursor: 'pointer',
  },
  questionTitleRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
    flex: 1,
    minWidth: 0,
  },
  questionTitle: {
    fontWeight: 700,
    fontSize: '1rem',
    color: '#111',
    lineHeight: 1.4,
  },
  typeChip: {
    backgroundColor: grey[200],
    color: '#444',
    height: 24,
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  scoreChip: {
    fontWeight: 700,
    height: 28,
    fontSize: '0.8125rem',
    color: '#fff',
  },
  scoreHigh: {
    backgroundColor: green[600],
  },
  scoreMedium: {
    backgroundColor: orange[600],
  },
  scoreLow: {
    backgroundColor: red[600],
  },
  chevron: {
    color: grey[600],
    fontSize: 22,
  },
  options: {
    marginTop: 20,
  },
  optionBlock: {
    marginBottom: 18,
    '&:last-child': {
      marginBottom: 0,
    },
  },
  optionRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 16,
  },
  optionLeft: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
    flex: '0 1 42%',
    minWidth: 160,
  },
  optionRight: {
    flex: '1 1 50%',
    minWidth: 160,
  },
  radioOuter: {
    width: 18,
    height: 18,
    borderRadius: '50%',
    border: `2px solid ${grey[400]}`,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxSizing: 'border-box',
  },
  radioCorrect: {
    borderColor: green[600],
    backgroundColor: green[600],
  },
  radioIncorrect: {
    borderColor: red[600],
    backgroundColor: red[600],
    color: '#fff',
    fontSize: '0.7rem',
    fontWeight: 700,
    lineHeight: 1,
  },
  checkboxOuter: {
    width: 18,
    height: 18,
    borderRadius: 4,
    border: `2px solid ${grey[400]}`,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontSize: '0.7rem',
    color: '#fff',
    fontWeight: 700,
    boxSizing: 'border-box',
  },
  checkboxCorrect: {
    borderColor: green[600],
    backgroundColor: green[600],
  },
  checkboxIncorrect: {
    borderColor: red[600],
    backgroundColor: red[600],
  },
  optionText: {
    fontSize: '0.9375rem',
    color: '#222',
    lineHeight: 1.4,
  },
  optionTextMuted: {
    color: grey[500],
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 10px',
    borderRadius: 12,
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  correctBadge: {
    backgroundColor: '#E8F5E9',
    color: green[800],
  },
  incorrectBadge: {
    backgroundColor: '#FFEBEE',
    color: red[800],
  },
  barTrack: {
    height: 10,
    borderRadius: 6,
    backgroundColor: grey[200],
    overflow: 'hidden',
    marginBottom: 4,
  },
  barFill: {
    height: '100%',
    borderRadius: 6,
    backgroundColor: grey[400],
    minWidth: 0,
    transition: 'width 0.2s ease',
  },
  barFillCorrect: {
    backgroundColor: green[600],
  },
  barFillIncorrect: {
    backgroundColor: red[500],
  },
  countLabel: {
    fontSize: '0.8125rem',
    color: '#666',
    marginBottom: 8,
  },
  students: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  studentChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px 4px 4px',
    borderRadius: 16,
    fontSize: '0.8125rem',
    fontWeight: 500,
  },
  studentChipCorrect: {
    backgroundColor: '#E8F5E9',
    color: green[900],
  },
  studentChipIncorrect: {
    backgroundColor: '#FFEBEE',
    color: red[900],
  },
  studentAvatar: {
    width: 22,
    height: 22,
    fontSize: '0.65rem',
    backgroundColor: grey[400],
  },
  emptyState: {
    color: '#666',
    fontSize: '0.875rem',
  },
});

const getStudentFullName = (student) =>
  [student.first_name, student.last_name].filter(Boolean).join(' ') ||
  student.email ||
  'Student';

const getStudentInitials = (student) => {
  const first = (student.first_name || '').charAt(0);
  const last = (student.last_name || '').charAt(0);
  const initials = `${first}${last}`.toUpperCase();
  return initials || '?';
};

const getQuestionDefinition = (quiz, questionIndex) => {
  const quizQuestion = getQuizQuestion(quiz, questionIndex) || {};
  const submissionQuestion =
    R.pipe(
      R.propOr([], 'submissions'),
      R.map((submission) => getSubmissionQuestion(submission, questionIndex)),
      R.find(
        (question) =>
          question &&
          (!R.isEmpty(getQuestionOptions(question)) ||
            question.title ||
            question.type)
      )
    )(quiz) || {};

  const quizOptions = getQuestionOptions(quizQuestion);
  const submissionOptions = getQuestionOptions(submissionQuestion);

  return {
    ...submissionQuestion,
    ...quizQuestion,
    index: questionIndex,
    title:
      quizQuestion.title ||
      submissionQuestion.title ||
      `Question ${questionIndex + 1}`,
    type: quizQuestion.type || submissionQuestion.type,
    options: !R.isEmpty(quizOptions) ? quizOptions : submissionOptions,
    solution: quizQuestion.solution || submissionQuestion.solution,
  };
};

const buildAnswerBreakdown = (quiz, questionIndex) => {
  const submissions = R.propOr([], 'submissions', quiz);
  const question = getQuestionDefinition(quiz, questionIndex);
  const options = getQuestionOptions(question);

  if (R.isEmpty(options)) return { question, options: [], totalResponses: 0 };

  const optionStats = options.map((option, index) => ({
    key: index,
    label: getOptionLabel(option, index),
    isCorrect: isOptionCorrectAnswer(question, index) === true,
    students: [],
  }));

  let answeredCount = 0;

  submissions.forEach((submission) => {
    const submissionQuestion = getSubmissionQuestion(submission, questionIndex);
    const studentChoices = getStudentChoices(submissionQuestion);
    if (R.isEmpty(studentChoices)) return;
    answeredCount += 1;
    studentChoices.forEach((choiceIndex) => {
      if (optionStats[choiceIndex]) {
        optionStats[choiceIndex].students.push(submission);
      }
    });
  });

  const optionsWithCount = optionStats.map((option) => ({
    ...option,
    count: option.students.length,
  }));

  return {
    question,
    options: optionsWithCount,
    totalResponses: answeredCount,
  };
};

const getScoreChipClass = (average, classes) => {
  if (average >= 70) return `${classes.scoreChip} ${classes.scoreHigh}`;
  if (average >= 40) return `${classes.scoreChip} ${classes.scoreMedium}`;
  return `${classes.scoreChip} ${classes.scoreLow}`;
};

const getOptionTone = (option) => {
  if (option.isCorrect) return 'correct';
  if (option.count > 0) return 'incorrect';
  return 'neutral';
};

const OptionIndicator = ({ tone, multiple, classes }) => {
  if (multiple) {
    return (
      <span
        className={`${classes.checkboxOuter} ${
          tone === 'correct'
            ? classes.checkboxCorrect
            : tone === 'incorrect'
            ? classes.checkboxIncorrect
            : ''
        }`}
      >
        {tone === 'correct' ? '✓' : tone === 'incorrect' ? '✕' : ''}
      </span>
    );
  }

  return (
    <span
      className={`${classes.radioOuter} ${
        tone === 'correct'
          ? classes.radioCorrect
          : tone === 'incorrect'
          ? classes.radioIncorrect
          : ''
      }`}
    >
      {tone === 'incorrect' ? '✕' : ''}
    </span>
  );
};

const StudentChip = ({ student, tone, classes }) => (
  <span
    className={`${classes.studentChip} ${
      tone === 'incorrect'
        ? classes.studentChipIncorrect
        : classes.studentChipCorrect
    }`}
  >
    <Avatar className={classes.studentAvatar}>
      {getStudentInitials(student)}
    </Avatar>
    {getStudentFullName(student)}
  </span>
);

const OptionBreakdown = ({ option, totalResponses, multiple, classes }) => {
  const width = totalResponses
    ? Math.round((100 * option.count) / totalResponses)
    : 0;
  const tone = getOptionTone(option);
  const showStudents = !R.isEmpty(option.students);

  return (
    <Box className={classes.optionBlock}>
      <Box className={classes.optionRow}>
        <Box className={classes.optionLeft}>
          <OptionIndicator tone={tone} multiple={multiple} classes={classes} />
          <Typography
            className={`${classes.optionText} ${
              tone === 'neutral' ? classes.optionTextMuted : ''
            }`}
            component="span"
          >
            {option.label}
          </Typography>
          {tone === 'correct' && (
            <span className={`${classes.statusBadge} ${classes.correctBadge}`}>
              Correct
            </span>
          )}
          {tone === 'incorrect' && (
            <span
              className={`${classes.statusBadge} ${classes.incorrectBadge}`}
            >
              Incorrect
            </span>
          )}
        </Box>
        <Box className={classes.optionRight}>
          <Box className={classes.barTrack}>
            {width > 0 && (
              <Box
                className={`${classes.barFill} ${
                  tone === 'correct'
                    ? classes.barFillCorrect
                    : tone === 'incorrect'
                    ? classes.barFillIncorrect
                    : ''
                }`}
                style={{ width: `${Math.max(width, 4)}%` }}
              />
            )}
          </Box>
          <Typography className={classes.countLabel}>
            {option.count} of {totalResponses}
          </Typography>
          {showStudents && (
            <Box className={classes.students}>
              {option.students.map((student) => (
                <StudentChip
                  key={student.email || student.submissionId || student.id}
                  student={student}
                  tone={tone}
                  classes={classes}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const QuestionCard = ({
  quiz,
  questionIndex,
  average,
  expanded,
  onToggle,
  cardRef,
}) => {
  const classes = useStyles();
  const breakdown = useMemo(
    () => buildAnswerBreakdown(quiz, questionIndex),
    [quiz, questionIndex]
  );
  const { question, options, totalResponses } = breakdown;
  const questionType = formatQuestionType(question.type);
  const multiple = isMultipleChoiceQuestion(question);
  const title =
    question.title ||
    question.prompt ||
    question.text ||
    `Question ${questionIndex + 1}`;

  return (
    <Box className={classes.questionCard} ref={cardRef}>
      <Box className={classes.questionHeader} onClick={onToggle}>
        <Box className={classes.questionTitleRow}>
          <Typography className={classes.questionTitle} component="div">
            Q{questionIndex + 1}. {title}
          </Typography>
          {questionType && (
            <Chip
              size="small"
              label={questionType}
              className={classes.typeChip}
            />
          )}
        </Box>
        <Box className={classes.headerRight}>
          {!R.isNil(average) && (
            <Chip
              size="small"
              label={`${Math.round(average)}%`}
              className={getScoreChipClass(average, classes)}
            />
          )}
          {expanded ? (
            <KeyboardArrowDown className={classes.chevron} />
          ) : (
            <KeyboardArrowRight className={classes.chevron} />
          )}
        </Box>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box className={classes.options}>
          {R.isEmpty(options) ? (
            <Typography className={classes.emptyState}>
              No answer breakdown available for this question.
            </Typography>
          ) : (
            options.map((option) => (
              <OptionBreakdown
                key={option.key}
                option={option}
                totalResponses={totalResponses}
                multiple={multiple}
                classes={classes}
              />
            ))
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const QuizQuestionsList = ({
  record: quiz,
  expandedQuestionIndex = null,
  navigationRequestId = null,
}) => {
  const classes = useStyles();
  const questionCount = getQuestionCount(quiz);
  const averages = getQuestionAverages(quiz);
  const [expandedIndices, setExpandedIndices] = useState(() =>
    R.isNil(expandedQuestionIndex) ? [] : [expandedQuestionIndex]
  );
  const questionRefs = useRef({});
  const lastNavigationRequestId = useRef(null);

  useEffect(() => {
    if (R.isNil(expandedQuestionIndex) || R.isNil(navigationRequestId)) return;
    if (lastNavigationRequestId.current === navigationRequestId) return;
    lastNavigationRequestId.current = navigationRequestId;
    setExpandedIndices((current) =>
      current.includes(expandedQuestionIndex)
        ? current
        : [...current, expandedQuestionIndex]
    );
  }, [expandedQuestionIndex, navigationRequestId, quiz.id]);

  useEffect(() => {
    if (R.isNil(expandedQuestionIndex) || R.isNil(navigationRequestId)) return;
    const target = questionRefs.current[expandedQuestionIndex];
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [expandedQuestionIndex, navigationRequestId, quiz.id]);

  const toggleQuestion = (questionIndex) => {
    setExpandedIndices((current) =>
      current.includes(questionIndex)
        ? current.filter((index) => index !== questionIndex)
        : [...current, questionIndex]
    );
  };

  if (!questionCount) {
    return (
      <Box className={classes.root}>
        <Typography className={classes.emptyState}>
          No question-level data available for this quiz.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {R.range(0, questionCount).map((questionIndex) => (
        <QuestionCard
          key={questionIndex}
          quiz={quiz}
          questionIndex={questionIndex}
          average={R.nth(questionIndex, averages)}
          expanded={expandedIndices.includes(questionIndex)}
          onToggle={() => toggleQuestion(questionIndex)}
          cardRef={(node) => {
            questionRefs.current[questionIndex] = node;
          }}
        />
      ))}
    </Box>
  );
};

export default QuizQuestionsList;
